import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type InputHTMLAttributes } from "react";
import {
  combinePhone,
  DEFAULT_PHONE_PREFIX,
  formatPhoneNumber,
  normalizePrefix,
  phoneParts,
} from "@/lib/phone";

type NativeInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "type" | "value" | "onChange" | "className" | "style"
>;

interface PhoneInputProps extends NativeInputProps {
  value: string;
  onChange: (fullPhone: string) => void;
  error?: string;
  required?: boolean;
  /** Applies to the wider, local-number field. */
  className?: string;
  style?: CSSProperties;
  containerClassName?: string;
  containerStyle?: CSSProperties;
  prefixStyle?: CSSProperties;
  prefixAriaLabel?: string;
}

const baseFieldStyle: CSSProperties = {
  minHeight: 44,
  background: "#fff",
  color: "#2a1f14",
  fontFamily: "DM Sans, sans-serif",
  fontSize: 14,
  outline: "none",
  boxSizing: "border-box",
};

export function PhoneInput({
  value,
  onChange,
  error,
  required = false,
  className,
  style,
  containerClassName,
  containerStyle,
  prefixStyle,
  prefixAriaLabel = "Kod kraju",
  id,
  name,
  placeholder = "Numer telefonu",
  autoComplete = "tel",
  onFocus,
  onBlur,
  ...inputProps
}: PhoneInputProps) {
  const initial = phoneParts(value);
  const [prefix, setPrefix] = useState(initial.prefix);
  const [number, setNumber] = useState(initial.number);
  const lastEmittedValue = useRef(value);
  const pendingSelection = useRef<{ input: HTMLInputElement; position: number } | null>(null);

  useLayoutEffect(() => {
    const selection = pendingSelection.current;
    if (!selection) return;
    pendingSelection.current = null;
    if (document.activeElement === selection.input) {
      selection.input.setSelectionRange(selection.position, selection.position);
    }
  }, [number]);

  useEffect(() => {
    if (value === lastEmittedValue.current) return;
    const next = phoneParts(value);
    setPrefix(next.prefix);
    setNumber(next.number);
    lastEmittedValue.current = value;
  }, [value]);

  const emit = (nextPrefix: string, nextNumber: string) => {
    const fullPhone = combinePhone(nextPrefix, nextNumber);
    lastEmittedValue.current = fullPhone;
    onChange(fullPhone);
  };

  const prefixDigits = prefix.replace(/\D/g, "").length;
  const maxLocalDigits = Math.max(0, 15 - prefixDigits);
  const { border: customBorder, ...styleWithoutBorder } = style ?? {};
  const border = error ? "1.5px solid #c0392b" : (customBorder ?? "1px solid rgba(42,31,20,0.25)");
  const sharedStyle: CSSProperties = { ...baseFieldStyle, ...styleWithoutBorder };
  const setNumberFromInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const rawValue = input.value;
    const cursorPosition = input.selectionStart ?? rawValue.length;
    const digitsBeforeCursor = rawValue.slice(0, cursorPosition).replace(/\D/g, "").length;
    const nextNumber = rawValue.replace(/\D/g, "").slice(0, maxLocalDigits);
    const formattedNumber = formatPhoneNumber(nextNumber);

    setNumber(nextNumber);
    emit(prefix, nextNumber);

    let digitsSeen = 0;
    let nextCursorPosition = 0;
    for (let index = 0; index < formattedNumber.length; index += 1) {
      if (/\d/.test(formattedNumber[index])) digitsSeen += 1;
      if (digitsSeen === digitsBeforeCursor) {
        nextCursorPosition = index + 1;
        break;
      }
    }
    if (digitsBeforeCursor > digitsSeen) nextCursorPosition = formattedNumber.length;
    pendingSelection.current = { input, position: nextCursorPosition };
  };

  return (
    <div>
      <div
        className={containerClassName}
        style={{
          display: "flex",
          width: "100%",
          minWidth: 0,
          overflow: "hidden",
          border,
          borderRadius: "var(--config-field-radius)",
          ...containerStyle,
        }}
      >
        <input
          type="tel"
          value={prefix}
          onChange={(event) => {
            const nextPrefix = normalizePrefix(event.target.value);
            setPrefix(nextPrefix);
            emit(nextPrefix, number);
          }}
          onFocus={onFocus}
          onBlur={onBlur}
          aria-label={prefixAriaLabel}
          autoComplete="tel-country-code"
          inputMode="tel"
          style={{
            ...sharedStyle,
            flex: "0 0 68px",
            width: 68,
            padding: "0 8px",
            textAlign: "center",
            borderTop: "none",
            borderRight: "none",
            borderBottom: "none",
            borderLeft: "none",
            ...prefixStyle,
          }}
        />
        <input
          {...inputProps}
          id={id}
          name={name}
          type="tel"
          value={formatPhoneNumber(number)}
          onChange={setNumberFromInput}
          onFocus={onFocus}
          onBlur={onBlur}
          required={required}
          aria-invalid={Boolean(error) || undefined}
          autoComplete={autoComplete}
          inputMode="tel"
          placeholder={placeholder}
          className={className}
          style={{
            ...sharedStyle,
            flex: "1 1 auto",
            minWidth: 0,
            width: "100%",
            borderTop: "none",
            borderRight: "none",
            borderBottom: "none",
            borderLeft: "1px solid rgba(42,31,20,0.16)",
          }}
        />
      </div>
      {error && (
        <p role="alert" style={{ fontFamily: "DM Sans, sans-serif", fontSize: 12, color: "#c0392b", margin: "5px 0 0" }}>
          {error}
        </p>
      )}
    </div>
  );
}